import { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';

import { resolveColumnKey } from '../utils/resolveColumnKey';
import { ITableColumn } from '../utils/types';

const moveInArray = (items: string[], key: string, targetIndex: number) => {
  const arr = _.clone(items);
  _.pull(arr, key);

  const clamped = _.clamp(targetIndex, 0, arr.length);
  arr.splice(clamped, 0, key);

  return arr;
};

export interface IColumnOrderState {
  order: string[];
}

const resolveUniqKey = (col: ITableColumn<any>) => {
  return col.id || resolveColumnKey(col);
};

export const useColumnOrder = <T extends object>(
  columns: ITableColumn<T>[],
  onColumnOrderChange?: (order: string[]) => void
) => {
  const initialOrder = useMemo(() => {
    return columns
      .map(col => resolveUniqKey(col))
      .filter((key): key is string => Boolean(key));
  }, [columns]);

  const [columnOrder, setColumnOrder] = useState<string[]>(initialOrder);

  const orderedColumns = useMemo(() => {
    const grouped = _.groupBy(columns, col =>
      resolveUniqKey(col) ? 'withKey' : 'withoutKey'
    );

    const columnsWithKeys = grouped.withKey ?? [];
    const columnsWithoutKeys = grouped.withoutKey ?? [];

    const sortedColumnsWithKeys = _.sortBy(columnsWithKeys, col => {
      const key = resolveUniqKey(col);
      const index = key ? columnOrder.indexOf(key) : -1;

      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    });

    return [...sortedColumnsWithKeys, ...columnsWithoutKeys];
  }, [columns, columnOrder]);

  const updateOrder = useCallback(
    (newOrder: string[]) => {
      setColumnOrder(newOrder);
      onColumnOrderChange?.(newOrder);
    },
    [onColumnOrderChange]
  );

  const moveColumnLeft = useCallback(
    (key: string) => {
      const index = columnOrder.indexOf(key);
      if (index <= 0) return;

      updateOrder(moveInArray(columnOrder, key, index - 1));
    },
    [columnOrder, updateOrder]
  );

  const moveColumnRight = useCallback(
    (key: string) => {
      const index = columnOrder.indexOf(key);
      if (index === -1 || index >= columnOrder.length - 1) return;

      updateOrder(moveInArray(columnOrder, key, index + 1));
    },
    [columnOrder, updateOrder]
  );

  const moveColumnFirst = useCallback(
    (key: string) => {
      updateOrder(moveInArray(columnOrder, key, 0));
    },
    [columnOrder, updateOrder]
  );

  const moveColumnLast = useCallback(
    (key: string) => {
      updateOrder(moveInArray(columnOrder, key, columnOrder.length - 1));
    },
    [columnOrder, updateOrder]
  );

  return {
    orderedColumns,
    columnOrder,
    moveColumnLeft,
    moveColumnRight,
    moveColumnFirst,
    moveColumnLast,
  };
};
